import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { EVENTS, publish, subscribe } from '@sendible/event-manager';
import { MediaFileType } from '../../models/medias/types';
import { BackgroundUploader } from '.';

export type BackgroundUploaderContextType = {
  addUploadAndReturnId: (name: string, type?: MediaFileType) => string;
  canBeClosed: boolean;
  canCancelUpload: (fileId: string) => boolean;
  cancelAllUploads: () => void;
  closeUploader: () => void;
  isMinimised: boolean;
  setIsMinimised: Dispatch<SetStateAction<boolean>>;
  updateFileError: (params: UpdateFileErrorParamsType) => void;
  updateFileErrorByMediaId: (mediaId: MediaIdType, errorCode: string) => void;
  updateFileProgress: (params: UpdateFileProgressParamsType) => void;
  updateFileName: (params: UpdateFileNameParamsType) => void;
  updateFileUploaded: (fileId: string, mediaId: MediaIdType) => void;
  updateFileMediaId: (params: { fileId: string; mediaId: MediaIdType }) => void;
  uploadingFiles: Record<string, UploadingFileProps>;
  uploadingFilesLength: number;
  uploadingMediasAmount: number;
  uploadedMediasAmount: number;
};

export const BackgroundUploaderContext = createContext({} as BackgroundUploaderContextType);

export const BackgroundUploaderProvider = ({ children }: Component) => {
  const [uploadingFiles, setUploadingFiles] = useState<Record<string, UploadingFileProps>>({});
  const [isMinimised, setIsMinimised] = useState(false);
  const [isClosed, setIsClosed] = useState(true);

  const canBeClosed = useMemo(() => Object.values(uploadingFiles).every(({ status }) => status === 'Ready' || status === 'Failed'), [uploadingFiles]);
  const resolvedIsMinimised = canBeClosed ? false : isMinimised;

  const setIsMinimisedSafely: Dispatch<SetStateAction<boolean>> = (value) => {
    if (canBeClosed) {
      setIsMinimised(false);

      return;
    }

    setIsMinimised(value);
  };
  const closeUploader = () => {
    setIsClosed(true);
    setUploadingFiles({});
  };

  const uploadingMediasAmount = Object.values(uploadingFiles).filter(({ status }) => status !== 'Ready' && status !== 'Failed').length;
  const uploadedMediasAmount = Object.values(uploadingFiles).filter(({ status }) => status === 'Ready').length;
  const uploadingFilesLength = Object.values(uploadingFiles).length;

  const addUploadAndReturnId = (name: string, type?: MediaFileType) => {
    const fileId = Math.random().toString();

    setIsMinimised(false);
    setIsClosed(false);

    setUploadingFiles((prev) => {
      return {
        ...prev,
        [fileId]: {
          id: fileId,
          name,
          mediaType: type || MediaFileType.Unspecified,
          status: 'Processing',
          uploadProgress: 1,
        },
      };
    });

    return fileId;
  };

  const updateFileProgress = ({ fileId, uploadProgress }: UpdateFileProgressParamsType) => {
    if (!uploadingFiles[fileId]) return;

    setUploadingFiles((items) => {
      if (items[fileId]) {
        return {
          ...items,
          [fileId]: {
            ...items[fileId],
            id: fileId,
            uploadProgress,
          },
        };
      }

      return { ...items };
    });
  };

  const updateFileError = ({ fileId, errorCode }: UpdateFileErrorParamsType) => {
    if (!uploadingFiles[fileId]) return;

    setIsMinimised(false);
    setUploadingFiles((items) => {
      if (items[fileId]) {
        return {
          ...items,
          [fileId]: {
            ...items[fileId],
            errorCode,
            status: 'Failed',
          },
        };
      }

      return { ...items };
    });
  };

  const updateFileErrorByMediaId = (mediaId: MediaIdType, errorCode: string) => {
    const uploadingFile = Object.values(uploadingFiles).find((file) => file.mediaId == mediaId);

    if (uploadingFile) {
      updateFileError({ fileId: uploadingFile.id, errorCode });
    }
  };

  const updateFileUploaded = (fileId: string, mediaId: MediaIdType) => {
    if (!uploadingFiles[fileId]) return;

    setUploadingFiles((items) => {
      if (items[fileId]) {
        return {
          ...items,
          [fileId]: {
            ...items[fileId],
            id: fileId,
            mediaId,
            status: 'Processing',
          },
        };
      }

      return { ...items };
    });
  };
  const updateFileReady = (media: MediaType) => {
    const mediaToUpdate = Object.values(uploadingFiles).find(({ mediaId: id }) => id == media.id);

    if (mediaToUpdate) {
      setUploadingFiles((items) => {
        if (items[mediaToUpdate.id]) {
          return {
            ...items,
            [mediaToUpdate.id]: {
              ...items[mediaToUpdate.id],
              status: 'Ready',
              mediaType: media.type,
            },
          };
        }

        return { ...items };
      });
    }
  };
  const updateFileMediaId = ({ fileId, mediaId }: { fileId: string; mediaId: MediaIdType }) => {
    setUploadingFiles((items) => {
      if (items[fileId]) {
        return {
          ...items,
          [fileId]: {
            ...items[fileId],
            mediaId,
          },
        };
      }

      return { ...items };
    });
  };

  const updateFileName = ({ fileId, name }: UpdateFileNameParamsType) => {
    setUploadingFiles((items) => {
      if (items[fileId]) {
        return {
          ...items,
          [fileId]: {
            ...items[fileId],
            name,
          },
        };
      }

      return { ...items };
    });
  };

  const canCancelUpload = (fileId: string) =>
    !!(
      uploadingFiles[fileId] &&
      !!uploadingFiles[fileId].mediaId &&
      (uploadingFiles[fileId].status === 'Processing' || uploadingFiles[fileId].status === 'New')
    );

  const cancelAllUploads = () => {
    Object.keys(uploadingFiles).forEach((fileId) => {
      const media = uploadingFiles[fileId];

      if (media.status === 'Ready' || media.status === 'Failed') return;

      if (media.mediaId) {
        // Already has a media record - the usual cancel path (also deletes it server-side).
        publish(EVENTS.UPLOAD_MEDIA_CANCELLED, media.mediaId);
      } else {
        // Still queued behind a concurrency limit (e.g. a bulk-import CSV batch) - there's no
        // media record yet for UPLOAD_MEDIA_CANCELLED to act on, so mark it cancelled directly.
        updateFileError({ fileId, errorCode: 'UploadCancelled' });
      }
    });

    // UPLOAD_MEDIA_CANCELLED above never reaches uploads that haven't started their network
    // request yet - tell any batch/queue-based upload runner (e.g. BulkImportUploadBridge) to
    // stop dispatching further queued items too, or they'd silently upload once a slot frees.
    publish(EVENTS.CANCEL_ALL_QUEUED_UPLOADS, {});
  };

  useEffect(() => {
    const readyUnsubscribe = subscribe(EVENTS.UPLOAD_READY, (media: MediaType) => {
      updateFileReady(media);
    });

    const failedUnsubscribe = subscribe(EVENTS.UPLOAD_FAILED, ({ fileId, mediaId, error }: FailedMediaType) => {
      if (fileId) {
        updateFileError({ fileId, errorCode: error });
      } else if (mediaId) {
        updateFileErrorByMediaId(mediaId, error);
      }
    });

    const uploadProgressUnsubscribe = subscribe(EVENTS.UPLOAD_PROGRESS, (params: UpdateFileProgressParamsType) => {
      updateFileProgress(params);
    });

    const cancelAllUnsubscribe = subscribe(EVENTS.CANCEL_ALL_UPLOADS_AND_CLOSE_BU, () => {
      Object.values(uploadingFiles).forEach((file) => {
        const { mediaId, status } = file;

        if (mediaId && status != 'Ready') publish(EVENTS.UPLOAD_MEDIA_CANCELLED, mediaId);
      });

      closeUploader();
    });

    return () => {
      readyUnsubscribe();
      failedUnsubscribe();
      uploadProgressUnsubscribe();
      cancelAllUnsubscribe();
    };
  }, [uploadingFiles]);

  return (
    <BackgroundUploaderContext.Provider
      value={{
        addUploadAndReturnId,
        canBeClosed,
        canCancelUpload,
        cancelAllUploads,
        closeUploader,
        isMinimised: resolvedIsMinimised,
        setIsMinimised: setIsMinimisedSafely,
        updateFileProgress,
        updateFileError,
        updateFileErrorByMediaId,
        updateFileName,
        updateFileUploaded,
        updateFileMediaId,
        uploadingFiles,
        uploadingFilesLength,
        uploadingMediasAmount,
        uploadedMediasAmount,
      }}
    >
      {!isClosed && createPortal(<BackgroundUploader />, document.getElementById('root') as HTMLElement)}
      {children}
    </BackgroundUploaderContext.Provider>
  );
};

export const useBackgroundUploaderContext = (): BackgroundUploaderContextType => useContext(BackgroundUploaderContext);
